/**
 * ============================================================================
 * Equipment 模块 API 工厂函数（模式一：单一资源）
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Equipment（设备管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createEquipmentApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * @module api/equipment/factory
 * @description Equipment（设备管理）模块的 API 工厂函数
 * @generated 2025-11-01
 * @author AI Assistant
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  Equipment,
  EquipmentQueryParams,
  CreateEquipmentForm,
  UpdateEquipmentForm,
  BindWorkOrderForm,
  BindOperatorForm,
  ChangeOperationForm,
  EquipmentRepair,
  BatchImportEquipmentItem,
  CreateInspectionForm,
  EquipmentInspectionRecord,
  SupportedOperation,
} from '../../types/equipment'

import type { PaginationResult, BatchImportResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// Equipment API 工厂函数
// ============================================================================

/**
 * 创建 Equipment 模块的 API 方法集合
 */
export function createEquipmentApi(client: IRequestClient) {
  return {
    /**
     * 获取设备列表（分页）
     */
    getEquipmentList(params?: EquipmentQueryParams) {
      return client.get<PaginationResult<Equipment>>(
        API_ROUTES.equipment.list,
        { params }
      )
    },
    
    /**
     * 获取设备详情
     */
    getEquipmentDetail(id: number) {
      return client.get<Equipment>(API_ROUTES.equipment.detail(id))
    },
    
    /**
     * 创建设备
     */
    createEquipment(data: CreateEquipmentForm) {
      return client.post<Equipment>(API_ROUTES.equipment.create, data)
    },
    
    /**
     * 更新设备
     */
    updateEquipment(id: number, data: UpdateEquipmentForm) {
      return client.put<Equipment>(API_ROUTES.equipment.equipments(id), data)
    },
    
    /**
     * 删除设备
     */
    deleteEquipment(id: number) {
      return client.delete<void>(API_ROUTES.equipment.delete(id))
    },
    
    /**
     * 扫码获取设备信息
     */
    scanEquipment(code: string) {
      return client.get<Equipment>(API_ROUTES.equipment.scan(code))
    },
    
    /**
     * 绑定工单
     */
    bindWorkOrder(id: number, data: BindWorkOrderForm) {
      return client.post<Equipment>(API_ROUTES.equipment.bindWorkOrder(id), data)
    },
    
    /**
     * 解绑工单
     */
    unbindWorkOrder(id: number) {
      return client.post<Equipment>(API_ROUTES.equipment.unbindWorkOrder(id))
    },

    /**
     * 获取我的设备（当前员工绑定的设备列表）
     * 
     * @description 获取当前登录用户绑定的所有设备
     * @returns 设备列表（不分页）
     */
    getMyEquipments() {
      return client.get<Equipment[]>(API_ROUTES.equipment.equipmentsMy)
    },

    /**
     * 设备上机（绑定操作员）
     * 
     * @description 员工扫码设备后上机，将自己绑定到该设备，并选择工序
     * 
     * 规则：
     * - 一人最多绑定6台设备
     * - 一机一绑（设备只能被一人绑定）
     * - 上机时必须选择工序
     * 
     * @param id 设备ID
     * @param data 上机请求（包含工序ID）
     */
    bindOperator(id: number, data: BindOperatorForm) {
      return client.post<Equipment>(API_ROUTES.equipment.bindOperator(id), data)
    },

    /**
     * 设备下机（解绑操作员）
     * 
     * @description 员工从设备下机，同时清空工序绑定
     * @param id 设备ID
     */
    unbindOperator(id: number) {
      return client.post<Equipment>(API_ROUTES.equipment.unbindOperator(id))
    },

    /**
     * 管理员强制下机
     *
     * @description 管理员处理员工忘记下机、离职交接等异常占机场景
     * @param id 设备ID
     * @param remark 强制下机原因
     */
    forceUnbindOperator(id: number, remark?: string) {
      return client.post<Equipment>(API_ROUTES.equipment.forceUnbindOperator(id), { remark })
    },

    /**
     * 更换工序
     * 
     * @description 上机后可以更换当前设备的工序
     * @param id 设备ID
     * @param data 更换工序请求
     */
    changeOperation(id: number, data: ChangeOperationForm) {
      return client.post<Equipment>(API_ROUTES.equipment.changeOperation(id), data)
    },

    /**
     * 修改设备状态（管理员功能）
     * 
     * @description 独立的状态修改接口，不需要传递其他字段
     * @param id 设备ID
     * @param status 新状态 (1:空闲, 2:运行中, 3:维修中, 4:保养中, 5:停用)
     */
    updateStatus(id: number, status: number) {
      return client.post<Equipment>(API_ROUTES.equipment.status(id), { status })
    },
    
    /**
     * 导出设备数据（开发中）
     */
    exportEquipments(params?: EquipmentQueryParams) {
      return client.get<Blob>(API_ROUTES.equipment.export, {
        params,
        responseType: 'blob',
      })
    },
    
    /**
     * 批量导入设备
     * 
     * @example
     * ```typescript
     * const result = await equipmentApi.batchImportEquipments([
     *   {
     *     equipment_code: 'EQ001',
     *     equipment_name: '数控车床',
     *     equipment_type: 'CNC车床',
     *     // ... 其他字段
     *   }
     * ])
     * 
     * // 处理结果
     * console.log(`成功: ${result.successCount}, 失败: ${result.failedCount}`)
     * result.failedItems.forEach(item => {
     *   console.log(`第 ${item.row} 行失败: ${item.error}`)
     * })
     * ```
     */
    batchImportEquipments(equipments: BatchImportEquipmentItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.equipment.import, {
        equipments,
      })
    },

    // ===== 设备维修管理 =====

    /**
     * 获取维修单列表（分页）
     */
    getRepairList(params?: any) {
      return client.get<PaginationResult<EquipmentRepair>>(
        API_ROUTES.equipment.repairs,
        { params }
      )
    },

    /**
     * 获取维修单详情
     */
    getRepairDetail(id: number) {
      return client.get<EquipmentRepair>(API_ROUTES.equipment.equipmentRepairs(id))
    },

    /**
     * 创建报修单
     */
    createRepair(data: any) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.repairs, data)
    },

    /**
     * 扫码报修（移动端）
     */
    scanReportRepair(data: { equipment_code: string; fault_description: string; [key: string]: any }) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.scanReport, data)
    },

    /**
     * 更新维修单
     */
    updateRepair(id: number, data: any) {
      return client.put<EquipmentRepair>(API_ROUTES.equipment.equipmentRepairs(id), data)
    },

    /**
     * 删除维修单
     */
    deleteRepair(id: number) {
      return client.delete<void>(API_ROUTES.equipment.equipmentRepairs(id))
    },

    /**
     * 派单
     *
     * assigned_to_employee_id 传 mes_employees.id（员工选择器语义），后端反查系统账号；
     * assigned_to_id 为 users.id 账号语义，两者二选一
     */
    assignRepair(id: number, data: { assigned_to_id?: number; assigned_to_employee_id?: number; assigned_to_name?: string }) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.assign(id), data)
    },

    /**
     * 开始维修
     */
    startRepair(id: number) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.start(id))
    },

    /**
     * 完成维修
     */
    completeRepair(id: number, data: { repair_description: string; labor_cost?: number; parts_cost?: number; other_cost?: number; remark?: string }) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.complete(id), data)
    },

    /**
     * 取消维修
     */
    cancelRepair(id: number, data: { reason: string }) {
      return client.post<EquipmentRepair>(API_ROUTES.equipment.cancel(id), data)
    },

    // ===== 设备保养管理 =====

    /**
     * 获取保养计划列表
     */
    getMaintenancePlanList(params?: any) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.equipment.plans,
        { params }
      )
    },

    /**
     * 获取保养计划详情
     */
    getMaintenancePlanDetail(id: number) {
      return client.get<any>(API_ROUTES.equipment.maintenancePlans(id))
    },

    /**
     * 创建保养计划
     */
    createMaintenancePlan(data: any) {
      return client.post<any>(API_ROUTES.equipment.plans, data)
    },

    /**
     * 更新保养计划
     */
    updateMaintenancePlan(id: number, data: any) {
      return client.put<any>(API_ROUTES.equipment.maintenancePlans(id), data)
    },

    /**
     * 删除保养计划
     */
    deleteMaintenancePlan(id: number) {
      return client.delete<void>(API_ROUTES.equipment.maintenancePlans(id))
    },

    /**
     * 获取保养任务列表
     */
    getMaintenanceTaskList(params?: any) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.equipment.tasks,
        { params }
      )
    },

    /**
     * 获取保养任务详情
     */
    getMaintenanceTaskDetail(id: number) {
      return client.get<any>(API_ROUTES.equipment.maintenanceTasks(id))
    },

    /**
     * 执行保养任务
     */
    executeMaintenanceTask(id: number, data: { maintenance_result?: string }) {
      return client.post<any>(API_ROUTES.equipment.execute(id), data)
    },

    // ===== 设备点检管理 =====

    /**
     * 获取点检记录列表
     */
    getInspectionList(params?: any) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.equipment.inspections,
        { params }
      )
    },

    /**
     * 获取点检记录详情
     */
    getInspectionDetail(id: number) {
      return client.get<any>(API_ROUTES.equipment.equipmentInspections(id))
    },

    /**
     * 创建点检记录
     * 
     * @description 提交设备点检结果
     * 
     * ⚠️ 重要：点检结果字段名是 `inspection_results`，不是 `inspection_items`
     * 
     * @example
     * ```typescript
     * await equipmentApi.createInspection({
     *   equipment_code: 'EQ001',
     *   inspection_type: 'DAILY',
     *   inspection_date: '2025-01-20',
     *   overall_result: 'PASS',
     *   inspection_results: [
     *     {
     *       item_name: '主轴运转',
     *       item_type: 'PASS_FAIL',
     *       result_value: 'PASS',
     *       is_qualified: true,
     *     },
     *     {
     *       item_name: '气压值',
     *       item_type: 'NUMBER',
     *       result_value: 5.5,
     *       is_qualified: true,
     *       min_value: 4.0,
     *       max_value: 6.0,
     *       unit: 'MPa',
     *     },
     *   ],
     * })
     * ```
     */
    createInspection(data: CreateInspectionForm) {
      return client.post<EquipmentInspectionRecord>(API_ROUTES.equipment.inspections, data)
    },

    // ===== 点检模板管理 =====

    /**
     * 获取点检模板列表
     */
    getInspectionTemplateList(params?: {
      equipment_type?: string
      inspection_type?: string
      status?: number
      keyword?: string
      per_page?: number
    }) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.equipment.inspectionTemplates,
        { params }
      )
    },

    /**
     * 获取点检模板详情
     */
    getInspectionTemplateDetail(id: number) {
      return client.get<any>(API_ROUTES.equipment.inspectionTemplateDetail(id))
    },

    /**
     * 创建点检模板
     */
    createInspectionTemplate(data: {
      template_name: string
      equipment_type?: string
      equipment_category?: string
      inspection_type: 'DAILY' | 'WEEKLY' | 'MONTHLY'
      description?: string
      is_default?: boolean
      items?: Array<{
        item_name: string
        item_type: string
        category?: string
        description?: string
        standard_value?: string
        min_value?: number
        max_value?: number
        unit?: string
        select_options?: Array<{ label: string; value: string }>
        is_required?: boolean
        photo_required?: boolean
      }>
    }) {
      return client.post<any>(API_ROUTES.equipment.inspectionTemplates, data)
    },

    /**
     * 更新点检模板
     */
    updateInspectionTemplate(id: number, data: any) {
      return client.put<any>(API_ROUTES.equipment.inspectionTemplateDetail(id), data)
    },

    /**
     * 删除点检模板
     */
    deleteInspectionTemplate(id: number) {
      return client.delete<any>(API_ROUTES.equipment.inspectionTemplateDetail(id))
    },

    /**
     * 复制点检模板
     */
    copyInspectionTemplate(id: number) {
      return client.post<any>(API_ROUTES.equipment.inspectionTemplateCopy(id))
    },

    // ===== 设备点检清单管理 =====

    /**
     * 获取所有点检清单列表（带分页）
     * 用于 Web 端点检清单绑定管理页面
     */
    getInspectionChecklistList(params?: {
      equipment_id?: number
      template_id?: number
      inspection_type?: string
      is_active?: boolean
      keyword?: string
      per_page?: number
      page?: number
    }) {
      return client.get<any>(API_ROUTES.equipment.inspectionChecklists, { params })
    },

    /**
     * 为设备绑定点检模板
     */
    bindInspectionChecklist(data: {
      equipment_id: number
      template_id: number
      inspection_type: 'DAILY' | 'WEEKLY' | 'MONTHLY'
      effective_date?: string
      expiry_date?: string
    }) {
      return client.post<any>(API_ROUTES.equipment.inspectionChecklistBind, data)
    },

    /**
     * 获取单个设备的点检清单列表
     */
    getEquipmentChecklists(equipmentId: number) {
      return client.get<any>(API_ROUTES.equipment.inspectionChecklistsByEquipment(equipmentId))
    },

    /**
     * 获取设备的点检项目（App端使用，支持扫码）
     */
    getEquipmentInspectionItems(params: {
      equipment_id?: number
      equipment_code?: string
      inspection_type?: 'DAILY' | 'WEEKLY' | 'MONTHLY'
    }) {
      return client.get<any>(API_ROUTES.equipment.inspectionChecklistItems, { params })
    },

    // ===== 设备支持工序管理 =====

    /**
     * 获取设备支持的工序列表
     * 
     * @description 获取设备已配置的支持工序
     * @param id 设备ID
     * @returns 包含设备信息、工序列表和限制状态的对象
     */
    getSupportedOperations(id: number) {
      return client.get<{
        equipment: {
          id: number
          equipmentCode: string
          equipmentName: string
        }
        operations: SupportedOperation[]
        operationRestrictionEnabled: boolean
      }>(API_ROUTES.equipment.supportedOperations(id))
    },

    /**
     * 同步设备支持的工序（批量更新，替换模式）
     * 
     * @description 批量更新设备支持的工序，传入的工序列表将完全替换现有关联
     * 
     * @param id 设备ID
     * @param data 工序同步数据
     * 
     * @example
     * ```typescript
     * // 设置设备支持工序 1、2、3，其中 1 为主工序
     * await equipmentApi.syncOperations(1, {
     *   operations: [
     *     { operation_id: 1, is_primary: true },
     *     { operation_id: 2, is_primary: false },
     *     { operation_id: 3, is_primary: false },
     *   ]
     * })
     * 
     * // 清空设备支持的工序
     * await equipmentApi.syncOperations(1, { operations: [] })
     * ```
     */
    syncOperations(id: number, data: {
      operations: Array<{
        operation_id: number
        is_primary?: boolean
        remark?: string
      }>
    }) {
      return client.post<{ syncedCount: number; operations: SupportedOperation[] }>(
        API_ROUTES.equipment.syncOperations(id),
        data
      )
    },

    /**
     * 批量为多个设备添加或同步工序
     *
     * @description
     * - append：仅向目标设备追加缺失工序，保留原有配置
     * - replace：用传入工序完全替换目标设备原有配置
     */
    batchSyncOperations(data: {
      equipment_ids: number[]
      operation_ids: number[]
      mode?: 'append' | 'replace'
      primary_operation_id?: number | null
      remark?: string | null
    }) {
      return client.post<{
        mode: 'append' | 'replace'
        equipmentCount: number
        operationCount: number
        affectedCount: number
        results: Array<{
          equipmentId: number
          equipmentCode: string
          equipmentName: string
          addedCount: number
          totalSupportedOperationCount: number
        }>
      }>(API_ROUTES.equipment.batchSyncOperations, data)
    },

    /**
     * 获取设备可选的工序列表（上机时使用）
     * 
     * @description 
     * 根据系统配置决定返回哪些工序：
     * - 如果开启了工序强制限制且设备配置了支持工序：只返回设备支持的工序
     * - 否则：返回所有启用的工序
     * 
     * 前端上机时应调用此接口获取可选工序列表，而不是直接调用工序全量列表
     * 
     * @param id 设备ID
     */
    getAvailableOperations(id: number) {
      return client.get<{
        /** 工序列表 */
        operations: Array<{
          id: number
          operationCode: string
          operationName: string
          operationType: string
          isPrimary?: boolean
        }>
        /** 是否受限（启用了工序限制且设备配置了支持的工序） */
        restricted: boolean
        /** 工序总数 */
        totalCount: number
      }>(API_ROUTES.equipment.availableOperations(id))
    },
  }
}
